/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import React from "react";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import PropTypes from "prop-types";

const ToggleButtons = ({ options, value, onChange, color = "primary" }) => (
  <ToggleButtonGroup
    value={value}
    color={color}
    exclusive
    onChange={(_, newValue) => {
      if (newValue !== null) onChange(newValue);
    }}
    sx={(theme) => {
      const theColor = theme.palette[color]?.main ?? theme.palette.primary.main;
      return {
        border: `1px solid ${theColor}`,
        overflow: "hidden",
        "& .MuiToggleButtonGroup-grouped": {
          color: theColor,
          fontSize: "14px",
          padding: "2px 16px",
          "&.Mui-selected": {
            backgroundColor: theColor,
            color: "#fff",
            "&:hover": { backgroundColor: theColor }
          },
          "&:hover": { backgroundColor: `${theColor}18` }
        }
      };
    }}
  >
    {options.map((option) => (
      <ToggleButton key={`toggle-btn-${option}`} value={option} disableRipple>
        {option}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

ToggleButtons.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  color: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

ToggleButtons.defaultProps = {
  value: null,
  color: "primary"
};

export default ToggleButtons;
